/**
 * React-query hooks for admin tenant payments (Payments page · Tab A).
 *
 * Source of truth: public.payments (the same table AdminAnalytics reads via
 * ['admin-analytics-payments']). Marking paid here updates the dashboard Rent
 * revenue + collection rate. Proof lives in public.payment_proofs + the
 * "contracts" storage bucket, matching the existing admin upload flow.
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  effectivePaymentStatus,
  paymentStatusInput,
} from "@/lib/paymentStatus";
import { resolveLeasePipelineStatus } from "@/lib/leaseStatus";
import { latestProofByPaymentId } from "@/lib/rent";
import { reportOwnerPaymentNotification } from "@/lib/owner-payment-notify-report";
import { PROPERTY_RENT_PAID_STATS_KEY } from "@/hooks/v2/usePropertyRentPaidStats";
import { invalidateOwnerCaches } from "@/hooks/v2/owner-detail-keys";

const PROOF_BUCKET = "contracts";

export const TENANT_PAYMENT_KEYS = {
  list: () => ["v2", "admin-tenant-payments", "list"] as const,
  proofs: () => ["v2", "admin-tenant-payments", "proofs"] as const,
  analytics: () => ["admin-analytics-payments"] as const,
};

export type TenantPaymentRow = {
  id: string;
  user_id: string | null;
  booking_id: number | null;
  type: string;
  amount_cents: number;
  currency: string;
  status: string;
  due_date: string | null;
  paid_at: string | null;
  created_at: string;
  bookings?: {
    id: number;
    status?: string | null;
    admin_archived_at?: string | null;
    contract_signed_at?: string | null;
    contract_status?: string | null;
    move_out_date?: string | null;
    actual_move_out_date?: string | null;
    end_date?: string | null;
    rooms?: {
      id: number;
      title: string | null;
      properties?: {
        id: number;
        title: string | null;
        city: string | null;
      } | null;
    } | null;
  } | null;
  stripe_payment_intent_id?: string | null;
  stripe_charge_id?: string | null;
  /** Admin attestation — who settled an off-platform payment, and when. */
  marked_paid_at?: string | null;
  marked_paid_by?: string | null;
  // Enriched client-side:
  tenantName?: string | null;
  tenantEmail?: string | null;
  proofPath?: string | null;
  proofStatus?: string | null;
  /** Canonical status for display (manual paid needs a proof or an attestation). */
  effectiveStatus?: string;
  moveOutDate?: string | null;
};

/**
 * Users with an active SEPA mandate (payment_methods.status = 'active',
 * type = 'sepa_debit'). Drives the "Collect SEPA" action — a debit can only
 * be triggered against tenants who authorized one (COL-89).
 */
export function useActiveSepaMandateUserIds() {
  return useQuery({
    queryKey: ["v2", "admin-sepa-mandates", "active-user-ids"],
    staleTime: 60_000,
    queryFn: async (): Promise<Set<string>> => {
      const { data, error } = await supabase
        .from("payment_methods")
        .select("user_id")
        .eq("type", "sepa_debit")
        .eq("status", "active");
      if (error) throw error;
      return new Set(
        ((data ?? []) as Array<{ user_id: string | null }>)
          .map((r) => r.user_id)
          .filter(Boolean) as string[]
      );
    },
  });
}

/**
 * Real SEPA state for one tenant, for the profile's payment-method card.
 * "Active" means the same thing it means everywhere else (Dashboard's
 * hasActiveMandate, rent-charge-tick eligibility): an ACTIVE sepa_debit
 * payment method backed by real Stripe objects — mock_* rows can never be
 * charged, so they don't count.
 */
export type TenantSepaStatus = {
  /** Both queries settled — render nothing until this is true. */
  ready: boolean;
  hasActiveMandate: boolean;
  /** Last 4 IBAN digits from the newest signed mandate, for display. */
  ibanLast4: string | null;
  /** Newest mandate row's status — distinguishes revoked/expired from never-signed. */
  mandateStatus: "active" | "revoked" | "expired" | null;
  /** Whether SEPA is offered for the tenant's room (room_payment_config opt-in). */
  roomSepaEnabled: boolean;
  /** Public URL of the signed mandate PDF (uploaded at creation), if any. */
  mandatePdfUrl: string | null;
  /** UMR of the newest mandate, shown alongside the embedded PDF. */
  mandateReference: string | null;
  /** Tenant's open request to end the mandate (feedback 6), if any. */
  pendingCancellation: {
    id: string;
    requested_method: string | null;
    created_at: string;
  } | null;
};

const isMockStripeId = (id: string | null | undefined) =>
  !!id && id.startsWith("mock_");

export function useTenantSepaStatus(
  userId: string | null,
  roomId: number | null
): TenantSepaStatus {
  const mandate = useQuery({
    queryKey: ["v2", "tenant-sepa-status", userId],
    enabled: !!userId,
    staleTime: 60_000,
    queryFn: async () => {
      const [methods, mandates, cancelRequests] = await Promise.all([
        supabase
          .from("payment_methods")
          .select("status, stripe_customer_id, stripe_payment_method_id")
          .eq("user_id", userId)
          .eq("type", "sepa_debit")
          .eq("status", "active"),
        supabase
          .from("sepa_mandates")
          .select("iban, status, signed_at, mandate_reference, pdf_url")
          .eq("user_id", userId)
          .order("signed_at", { ascending: false })
          .limit(1),
        supabase
          .from("sepa_mandate_cancellation_requests")
          .select("id, requested_method, created_at")
          .eq("user_id", userId)
          .eq("status", "pending")
          .limit(1),
      ]);
      if (methods.error) throw methods.error;
      if (mandates.error) throw mandates.error;
      if (cancelRequests.error) throw cancelRequests.error;
      const active = ((methods.data ?? []) as any[]).some(
        (m) =>
          !isMockStripeId(m.stripe_customer_id) &&
          !isMockStripeId(m.stripe_payment_method_id)
      );
      const newest = ((mandates.data ?? []) as any[])[0] ?? null;
      return {
        hasActiveMandate: active,
        ibanLast4: newest?.iban?.replace(/\s+/g, "").slice(-4) ?? null,
        mandateStatus: (newest?.status ?? null) as
          | "active"
          | "revoked"
          | "expired"
          | null,
        mandatePdfUrl: (newest?.pdf_url as string | null) ?? null,
        mandateReference: (newest?.mandate_reference as string | null) ?? null,
        pendingCancellation:
          ((cancelRequests.data ?? [])[0] as
            | {
                id: string;
                requested_method: string | null;
                created_at: string;
              }
            | undefined) ?? null,
      };
    },
  });
  const roomConfig = useQuery({
    // Distinct key from Dashboard's ["room-payment-methods"] — that one caches
    // all three flags; sharing the key would let this narrower read shadow it.
    queryKey: ["v2", "room-sepa-enabled", roomId],
    enabled: !!roomId,
    staleTime: 60_000,
    queryFn: async () => {
      // The resolver, not the room row: direct debit may be switched on at the
      // apartment or the property, and it is forced off under owner_collects.
      // Reading room_payment_config directly would offer a mandate for a room
      // the charge functions will never debit (and vice versa).
      const { data, error } = await supabase.rpc("get_room_payment_methods", {
        p_room_id: roomId,
      });
      if (error) throw error;
      return Array.isArray(data) ? (data[0] ?? null) : (data ?? null);
    },
  });
  return {
    ready:
      (!userId || !mandate.isLoading) && (!roomId || !roomConfig.isLoading),
    hasActiveMandate: mandate.data?.hasActiveMandate ?? false,
    ibanLast4: mandate.data?.ibanLast4 ?? null,
    mandateStatus: mandate.data?.mandateStatus ?? null,
    roomSepaEnabled: roomConfig.data?.sepa_debit_enabled === true,
    mandatePdfUrl: mandate.data?.mandatePdfUrl ?? null,
    mandateReference: mandate.data?.mandateReference ?? null,
    pendingCancellation: mandate.data?.pendingCancellation ?? null,
  };
}

export type PaymentProof = {
  id: string;
  payment_id: string;
  file_path: string;
  file_name: string;
  status: string;
  created_at: string;
};

export function useTenantPayments() {
  return useQuery({
    queryKey: TENANT_PAYMENT_KEYS.list(),
    queryFn: async (): Promise<TenantPaymentRow[]> => {
      const { data, error } = await supabase
        .from("payments")
        .select(
          `id, user_id, booking_id, type, amount_cents, received_cents, currency, status, due_date, paid_at, created_at,
           stripe_payment_intent_id, stripe_charge_id, marked_paid_at, marked_paid_by,
           bookings:booking_id ( id, status, admin_archived_at, contract_signed_at, contract_status, move_out_date, actual_move_out_date, end_date, rooms:room_id ( id, title, properties:property_id ( id, title, city ) ) )`
        )
        .in("type", ["rent", "deposit"])
        .order("due_date", { ascending: false })
        .limit(1000);
      if (error) throw error;
      const rows = (data ?? []) as TenantPaymentRow[];

      // Enrich with tenant name/email + latest proof path (two small follow-up queries).
      const userIds = [
        ...new Set(rows.map((r) => r.user_id).filter(Boolean)),
      ] as string[];
      const paymentIds = rows.map((r) => r.id);

      const [profilesRes, proofsRes] = await Promise.all([
        userIds.length
          ? supabase
              .from("profiles")
              .select("id, name, email")
              .in("id", userIds)
          : Promise.resolve({ data: [] }),
        paymentIds.length
          ? supabase
              .from("payment_proofs")
              .select("id, payment_id, file_path, status, created_at")
              .in("payment_id", paymentIds)
              .order("created_at", { ascending: false })
          : Promise.resolve({ data: [] }),
      ]);

      const nameById = new Map<
        string,
        { name: string | null; email: string | null }
      >();
      (profilesRes.data ?? []).forEach((p: any) =>
        nameById.set(p.id, { name: p.name, email: p.email })
      );
      // Latest proof per payment (query is ordered created_at desc).
      const proofByPayment = latestProofByPaymentId(
        (proofsRes.data ?? []) as Array<{
          payment_id: string;
          file_path: string;
          status: string | null;
        }>
      );

      const enriched = rows.map((r) => {
        const proof = proofByPayment.get(r.id) ?? null;
        const isStripe = !!(r.stripe_payment_intent_id || r.stripe_charge_id);
        return {
          ...r,
          tenantName: r.user_id
            ? (nameById.get(r.user_id)?.name ?? null)
            : null,
          tenantEmail: r.user_id
            ? (nameById.get(r.user_id)?.email ?? null)
            : null,
          proofPath: proof?.file_path ?? null,
          proofStatus: proof?.status ?? null,
          effectiveStatus: effectivePaymentStatus(
            paymentStatusInput(r, proof, { isStripe })
          ),
          moveOutDate:
            r.bookings?.move_out_date ??
            r.bookings?.actual_move_out_date ??
            r.bookings?.end_date ??
            null,
        };
      });

      // Deposit is "paid" for a booking when any of its deposit rows resolves to
      // paid (canonical #743 rule: Stripe paid, or an approved manual proof) —
      // the same input resolveLeasePipelineStatus needs to reach "active".
      const depositPaidBookingIds = new Set<number>();
      for (const r of enriched) {
        if (
          r.type === "deposit" &&
          r.effectiveStatus === "paid" &&
          r.booking_id != null
        )
          depositPaidBookingIds.add(r.booking_id);
      }

      // Show payments only for tenants the Bookings & Tenants overview would call
      // "Active" (leasePipelineStatus === "active"): a signed contract + paid
      // deposit, or an explicit bookings.status = active. Rows without a booking
      // can't be tied to an active tenant, so they're excluded too.
      return enriched.filter((r) => {
        if (!r.bookings) return false;
        const status = resolveLeasePipelineStatus(
          r.bookings,
          r.booking_id != null && depositPaidBookingIds.has(r.booking_id)
        );
        return status === "active";
      });
    },
  });
}

export function useTenantPaymentMutations() {
  const qc = useQueryClient();
  const invalidate = () => {
    // Payments is the single source of truth: refresh every cache that reads it
    // so a status change reflects instantly everywhere. React Query matches by
    // key prefix, so ["payments"] also covers ["payments", userId] etc.
    [
      TENANT_PAYMENT_KEYS.list(),
      TENANT_PAYMENT_KEYS.analytics(), // dashboard rent revenue + collection rate
      ["admin-reminders"],
      ["admin-payments"], // AdminTenants payments list
      ["admin-bookings-rent-payments"], // AdminTenants rent status
      ["admin-bookings-payments-for-status"],
      ["payments"], // tenant portal (Dashboard) + tenant detail
      ["payments-upcoming"],
      ["payment-proofs"],
      [PROPERTY_RENT_PAID_STATS_KEY], // admin properties grid pill
    ].forEach((queryKey) =>
      qc.invalidateQueries({ queryKey: queryKey as any })
    );
    // A rent payment settling can flip a company_collects group's payout
    // readiness (COL-461) — the owners-list stat tile reads this too.
    invalidateOwnerCaches(qc);
  };

  const uploadProof = useMutation({
    mutationFn: async ({
      paymentId,
      file,
    }: {
      paymentId: string;
      file: File;
    }) => {
      const ext = file.name.split(".").pop() || "pdf";
      const safe = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      const path = `payment-proofs/${paymentId}/${Date.now()}-admin-${safe}.${ext}`;
      const up = await supabase.storage
        .from(PROOF_BUCKET)
        .upload(path, file, { upsert: false });
      if (up.error) throw up.error;
      // Insert as pending_review so it does NOT auto-mark paid — the admin then
      // explicitly clicks "Mark paid" (the proof-gated two-step flow).
      const { error } = await supabase.from("payment_proofs").insert({
        payment_id: paymentId,
        file_path: path,
        file_name: file.name,
        file_size: file.size,
        mime_type: file.type || null,
        status: "pending_review",
      });
      if (error) {
        await supabase.storage.from(PROOF_BUCKET).remove([path]);
        throw error;
      }
    },
    onSuccess: invalidate,
  });

  const markPaid = useMutation({
    mutationFn: async (row: { id: string; proofPath?: string | null }) => {
      if (!row.proofPath) {
        throw new Error(
          "Upload a payment proof before marking this payment paid."
        );
      }
      // Who is approving it. Recorded because the proof row is the ONLY place
      // this path can name the acting admin, and mark_payment_paid_from_proof
      // (20260904150000) carries `reviewed_by` onto the payment's attestation
      // columns — without it, a month settled from here shows no admin on the
      // owner profile's Email history card while the same action from the
      // tenant profile shows one.
      const { data: authData } = await supabase.auth.getUser();
      const reviewerId = authData?.user?.id ?? null;

      // Approve the proof rather than raw-setting payments.status. The
      // mark_payment_paid_from_proof trigger then flips the payment to paid,
      // so PaymentsHub, AdminTenants, AdminPayments and the tenant Dashboard —
      // which all treat "paid" as requiring an APPROVED proof (#743) — agree.
      const { error } = await supabase
        .from("payment_proofs")
        .update({
          status: "approved",
          reviewed_at: new Date().toISOString(),
          reviewed_by: reviewerId,
        })
        .eq("payment_id", row.id)
        .neq("status", "approved");
      if (error) throw error;

      // Owner "Security Deposit Received" / "Rent Payment Received" mail —
      // the automated paid paths (Stripe webhook, session confirm) fire this
      // server-side at the point they flip the row; this manual-proof path
      // has no such hook, since the flip itself happens inside the
      // mark_payment_paid_from_proof trigger above, not in this mutation. The
      // trigger runs synchronously before the UPDATE above resolves, so this
      // read sees the post-trigger row. Fire-and-forget, same shape as the
      // other owner-lifecycle invokes — dispatched by the shared helper so
      // every client-side mark-paid surface notifies identically.
      const { data: paymentRow } = await supabase
        .from("payments")
        .select("type, booking_id, amount_cents, due_date")
        .eq("id", row.id)
        .maybeSingle();
      void reportOwnerPaymentNotification({
        ...(paymentRow ?? {}),
        id: row.id,
      });
    },
    onSuccess: invalidate,
  });

  const unmarkPaid = useMutation({
    mutationFn: async (id: string) => {
      // Demote the proof back to review AND reset the payment, so no surface
      // is left showing paid via a still-approved proof.
      const { error: proofErr } = await supabase
        .from("payment_proofs")
        .update({ status: "pending_review", reviewed_at: null })
        .eq("payment_id", id);
      if (proofErr) throw proofErr;
      const { error } = await supabase
        .from("payments")
        .update({ status: "pending", paid_at: null })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  const deleteProof = useMutation({
    mutationFn: async (paymentId: string) => {
      // Remove the stored files first, then the proof rows.
      const { data: proofs } = await supabase
        .from("payment_proofs")
        .select("id, file_path")
        .eq("payment_id", paymentId);
      const paths = (proofs ?? []).map((p: any) => p.file_path).filter(Boolean);
      if (paths.length) await supabase.storage.from(PROOF_BUCKET).remove(paths);
      const { error } = await supabase
        .from("payment_proofs")
        .delete()
        .eq("payment_id", paymentId);
      if (error) throw error;
      // Without a proof the payment can't be effectively paid — reset a stale
      // paid status so it doesn't linger as paid in the revenue dashboard.
      const { error: payErr } = await supabase
        .from("payments")
        .update({ status: "pending", paid_at: null })
        .eq("id", paymentId)
        .eq("status", "paid");
      if (payErr) throw payErr;
    },
    onSuccess: invalidate,
  });

  // COL-89 charge triggering: collect an unpaid payment via the tenant's
  // SEPA mandate. Calls create-rent-payment with { paymentId, targetUserId }
  // (admin-on-behalf) — the function creates the off-session Stripe debit and
  // the webhook settles it days later; the row shows "scheduled" meanwhile.
  const collectSepa = useMutation({
    mutationFn: async (row: { id: string; user_id: string | null }) => {
      if (!row.user_id) {
        throw new Error("Payment has no tenant account to collect from.");
      }
      const { data, error } = await supabase.functions.invoke(
        "create-rent-payment",
        {
          body: { paymentId: row.id, targetUserId: row.user_id },
        }
      );
      if (error) {
        // supabase-js wraps non-2xx responses; surface the function's message.
        let message = error.message ?? "SEPA collection failed";
        try {
          const body = await (error as any).context?.json?.();
          if (body?.error) message = body.error;
        } catch {
          /* keep the generic message */
        }
        throw new Error(message);
      }
      if (data?.error) throw new Error(String(data.error));
      return data;
    },
    onSuccess: invalidate,
  });

  return { uploadProof, markPaid, unmarkPaid, deleteProof, collectSepa };
}
